import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getSafeDetails, updateSafe, clearError, clearSuccess, clearSafeDetails } from '../../../../redux/Slices/authSlice';
import { FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';


// edit safe component
const EditSafe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { safeDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    is_active: true,
    is_default: false,
    notes: ''
  });

  useEffect(() => {
    if (id) {
      dispatch(getSafeDetails(id));
    }
    return () => {
      dispatch(clearSafeDetails());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (safeDetails) {
      const details = safeDetails.data || safeDetails;
      setFormData({
        name_en: details.name_en || details.name || '',
        name_ar: details.name_ar || details.name || '',
        is_active: details.is_active !== undefined ? details.is_active : true,
        is_default: details.is_default !== undefined ? details.is_default : false,
        notes: details.notes || ''
      });
    }
  }, [safeDetails]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/safes');
      }, 1500);
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      is_active: formData.is_active ? true : false,
      is_default: formData.is_default ? true : false
    };
    dispatch(updateSafe({ id, data: submitData }));
  };

  if (isLoading && !safeDetails) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {t('safes.form.loading')}
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/safes')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}
        >
          <FaArrowRight />
          {t('safes.form.back')}
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, textAlign }}>{t('safes.form.editTitle')}</h1>
      </div>

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '100%'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign }}>
              {t('safes.form.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign }}>
              {t('safes.form.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <label style={{ fontSize: '14px', fontWeight: 'bold', color: 'white', cursor: 'pointer', textAlign }}>
              {t('safes.form.isActive')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
            <input
              type="checkbox"
              name="is_default"
              checked={formData.is_default}
              onChange={handleChange}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <label style={{ fontSize: '14px', fontWeight: 'bold', color: 'white', cursor: 'pointer', textAlign }}>
              {t('safes.form.isDefault')}
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign }}>
            {t('safes.form.notes')}
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
          <button
            type="button"
            onClick={() => navigate('/safes')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {t('safes.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? t('safes.form.saving') : t('safes.form.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSafe;

